import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Fab,
  Zoom,
  Tooltip
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  PersonAdd as PersonAddIcon,
  Groups as GroupsIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import useOrganizationSetupStore from '@/store/OrganizationSetupStore';
import { LeadershipAssignment } from '@/services/OrganizationSetupService';
import LeaderCard from './LeaderCard';

interface LeadershipSectionProps {
  canEdit: boolean;
}

const LeadershipSection: React.FC<LeadershipSectionProps> = ({ canEdit }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [leaderToDelete, setLeaderToDelete] = useState<LeadershipAssignment | null>(null);

  const {
    leadershipHierarchy,
    hierarchyLoading,
    loading,
    removeAssignment
  } = useOrganizationSetupStore();

  // Group leaders by position level
  const groupedLeaders = leadershipHierarchy.reduce((acc, leader) => {
    const level = leader.position_level;
    if (!acc[level]) {
      acc[level] = [];
    }
    acc[level].push(leader);
    return acc;
  }, {} as Record<number, LeadershipAssignment[]>);

  const levelLabels: Record<number, string> = {
    1: 'Executive Leadership (C-Suite)',
    2: 'Vice Presidents',
    3: 'Directors',
    4: 'Senior Managers'
  };

  const handleAddLeader = () => {
    navigate('/organization/assign-leader');
  };

  const handleEditLeader = (leader: LeadershipAssignment) => {
    navigate(`/organization/assign-leader/${leader.id}`);
  };

  const handleDeleteLeader = (leader: LeadershipAssignment) => {
    setLeaderToDelete(leader);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (leaderToDelete) {
      await removeAssignment(leaderToDelete.id);
      setDeleteDialogOpen(false);
      setLeaderToDelete(null);
    }
  };

  // Loading State
  if (hierarchyLoading && leadershipHierarchy.length === 0) {
    return (
      <Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map(i => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={i}>
              <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${theme.palette.divider}` }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Skeleton variant="circular" width={80} height={80} />
                    <Skeleton variant="text" width={100} height={20} sx={{ mt: 2 }} />
                    <Skeleton variant="text" width={150} height={30} />
                    <Skeleton variant="text" width={180} height={20} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Empty State
  if (leadershipHierarchy.length === 0) {
    return (
      <Box>
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `2px dashed ${theme.palette.divider}`,
            p: 6,
            textAlign: 'center'
          }}
        >
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: theme.palette.primary.main + '15',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3
            }}
          >
            <GroupsIcon sx={{ fontSize: 48, color: theme.palette.primary.main }} />
          </Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            No Leadership Team Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
            Start building your organization's leadership structure by adding executives and managers.
          </Typography>
          {canEdit && (
            <Button
              variant="contained"
              size="large"
              startIcon={<PersonAddIcon />}
              onClick={handleAddLeader}
              sx={{ borderRadius: 2, px: 4 }}
            >
              Add First Leader
            </Button>
          )}
        </Card>
      </Box>
    );
  }

  // Main Content
  return (
    <Box>
      {/* Header with Add Button */}
      {canEdit && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddLeader}
            sx={{ borderRadius: 2 }}
          >
            Add Leader
          </Button>
        </Box>
      )}

      {/* Leaders grouped by level */}
      {Object.entries(groupedLeaders)
        .sort(([a], [b]) => Number(a) - Number(b))
        .map(([level, leaders]) => (
          <Box key={level} sx={{ mb: 4 }}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                mb: 2,
                pb: 1,
                borderBottom: `2px solid ${theme.palette.primary.main}`,
                display: 'inline-block'
              }}
            >
              {levelLabels[Number(level)] || `Level ${level}`}
            </Typography>
            <Grid container spacing={3}>
              {leaders.map(leader => (
                <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={leader.id}>
                  <LeaderCard
                    leader={leader}
                    canEdit={canEdit}
                    onEdit={handleEditLeader}
                    onDelete={handleDeleteLeader}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

      {/* Floating Action Button (Alternative Add) */}
      {canEdit && (
        <Zoom in>
          <Tooltip title="Add Leader">
            <Fab
              color="primary"
              onClick={handleAddLeader}
              sx={{
                position: 'fixed',
                bottom: 32,
                right: 32,
                display: { xs: 'flex', md: 'none' }
              }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        </Zoom>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>Remove Leadership Assignment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to remove{' '}
            <strong>{leaderToDelete?.employee_name}</strong> from the{' '}
            <strong>{leaderToDelete?.position_title}</strong> position?
            <br /><br />
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeadershipSection;
